import { useEffect, useState } from 'react';

import {
  useConnectPartner,
  useConnectorPresets,
  useCreateDataConnector,
  useDataConnectors,
  useDeleteDataConnector,
  useIssuePairingCode,
  usePartnerLinks,
  useRevokePartnerLink,
  useTestDataConnector,
  type ConnectorPresetRow,
  type DataConnectorRow,
  type PartnerLinkRow,
} from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  ConfirmModal,
  DrawerPanel,
  Field,
  Input,
  PageHeader,
} from '../../components/ui/index.js';

/**
 * ОТКУДА БРАТЬ ДАННЫЕ О ПОКУПКАХ
 *
 * Список систем приходит с сервера (`/data-connectors/presets`) — здесь нет
 * ни одного зашитого названия. Новая платформа появляется в этом списке сама,
 * вместе с полями своей формы подключения.
 *
 * Два способа подключения различаются принципиально и потому выглядят
 * по-разному:
 *
 *  - связывание аккаунтов — одна кнопка, продавец подтверждает доступ
 *    в своём же кабинете и не вводит никаких ключей;
 *  - код сопряжения — для сайтов с нашим плагином (WordPress): выпускаем
 *    код здесь, администратор сайта вставляет его в плагин;
 *  - ручной ключ — форма, потому что у Битрикса или WooCommerce взять доступ
 *    иначе неоткуда.
 */
export function SettingsDataConnectors(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;

  const presetsQ = useConnectorPresets(slug);
  const connectorsQ = useDataConnectors(slug);
  const linksQ = usePartnerLinks(slug);

  const createConnector = useCreateDataConnector(slug);
  const connectPartner = useConnectPartner(slug);
  const issuePairing = useIssuePairingCode(slug);

  const [manualPreset, setManualPreset] = useState<ConnectorPresetRow | null>(null);
  const [linkPreset, setLinkPreset] = useState<ConnectorPresetRow | null>(null);
  const [pairing, setPairing] = useState<{ preset: ConnectorPresetRow; code: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  // Куда вернуть продавца после связывания: пришёл по ссылке из кабинета
  // платформы — туда же и вернётся (сервер проверит, что адрес доверенный).
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const presets = presetsQ.data ?? [];
  const connectors = connectorsQ.data ?? [];
  const links = linksQ.data ?? [];

  /**
   * Переход по кнопке «Подключить чат» из кабинета платформы:
   * `?connect=<платформа>&base_url=…&return_to=…`. Открываем связывание сразу,
   * с подставленным адресом, — продавцу не нужно знать, что это за адрес.
   */
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const wanted = q.get('connect');
    if (!wanted || presets.length === 0) return;
    const preset = presets.find((p) => p.id === wanted && p.auth_mode === 'oauth_link');
    if (!preset) return;
    setError(null);
    setForm({ base_url: q.get('base_url') ?? '' });
    setReturnTo(q.get('return_to'));
    setLinkPreset(preset);
    window.history.replaceState({}, '', window.location.pathname);
  }, [presets]);

  function openManual(preset: ConnectorPresetRow): void {
    setError(null);
    setForm({ name: preset.title });
    setManualPreset(preset);
  }

  function openLink(preset: ConnectorPresetRow): void {
    setError(null);
    setForm({ base_url: '' });
    setLinkPreset(preset);
  }

  async function submitManual(): Promise<void> {
    if (!manualPreset) return;
    setError(null);
    try {
      await createConnector.mutateAsync({
        kind: manualPreset.id,
        name: form.name?.trim() || manualPreset.title,
        auth: {
          type: presetAuthType(manualPreset),
          base_url: form.base_url ?? '',
          ...(form.username ? { username: form.username } : {}),
        },
        ...(form.secret ? { secret: form.secret } : {}),
        // Запросы и таблица соответствий берутся из пресета на сервере —
        // клиент их не сочиняет.
        endpoints: {},
        cache_ttl_sec: manualPreset.defaults.cache_ttl_sec,
        rate_limit_per_min: manualPreset.defaults.rate_limit_per_min,
        timeout_ms: manualPreset.defaults.timeout_ms,
        allow_mutations: manualPreset.defaults.allow_mutations,
      });
      setManualPreset(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось подключить');
    }
  }

  async function submitLink(): Promise<void> {
    if (!linkPreset) return;
    setError(null);
    try {
      const res = await connectPartner.mutateAsync({
        provider: linkPreset.id,
        baseUrl: (form.base_url ?? '').trim(),
        returnTo: returnTo ?? `${window.location.origin}/settings/data-connectors`,
      });
      // Подтверждать доступ должен человек, уже вошедший в тот кабинет,
      // поэтому уводим браузер туда целиком, а не запрашиваем фоном.
      window.location.href = res.authorize_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось начать связывание');
    }
  }

  async function openPairing(preset: ConnectorPresetRow): Promise<void> {
    setError(null);
    setCopied(false);
    try {
      const res = await issuePairing.mutateAsync(preset.id);
      setPairing({ preset, code: res.pairing_code });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось выпустить код');
    }
  }

  const linkedProviders = new Set(links.filter((l) => l.status === 'active').map((l) => l.provider));

  return (
    <div className="inbx-page">
      <PageHeader title="Данные о покупках" />
      <div className="inbx-page__body">
        <p style={{ color: 'var(--inbx-text-tertiary)', marginTop: 0 }}>
          Оператор видит заказы и товары клиента прямо в карточке тикета. Запросы идут с нашего
          сервера — ключи в браузер не попадают.
        </p>

        {links.length > 0 && (
          <>
            <h4 style={{ margin: '16px 0 12px', fontSize: 'var(--inbx-fz-md)' }}>
              Связанные аккаунты
            </h4>
            <div className="inbx-grid-3">
              {links.map((link) => (
                <LinkCard key={link.id} link={link} slug={slug} />
              ))}
            </div>
          </>
        )}

        {connectors.length > 0 && (
          <>
            <h4 style={{ margin: '24px 0 12px', fontSize: 'var(--inbx-fz-md)' }}>Источники</h4>
            <div className="inbx-grid-3">
              {connectors.map((connector) => (
                <ConnectorCard
                  key={connector.id}
                  connector={connector}
                  preset={presets.find((p) => p.id === connector.kind)}
                  slug={slug}
                />
              ))}
            </div>
          </>
        )}

        <h4 style={{ margin: '24px 0 12px', fontSize: 'var(--inbx-fz-md)' }}>
          Доступные к подключению
        </h4>
        {error && !linkPreset && !manualPreset && (
          <div style={{ color: 'var(--inbx-danger, #b3261e)', marginBottom: 8 }}>{error}</div>
        )}
        {presetsQ.isLoading && <div>Загружаем список…</div>}
        <div className="inbx-grid-3">
          {presets.map((preset) => (
            <div
              key={preset.id}
              className="inbx-card"
              style={{ display: 'flex', gap: 12, alignItems: 'center' }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: 'var(--inbx-bg-subtle, #F1EFE8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className={`ti ${preset.icon}`} style={{ fontSize: 20 }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{preset.title}</div>
                <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
                  {preset.description}
                </div>
              </div>
              {preset.auth_mode === 'oauth_link' ? (
                <Button
                  size="sm"
                  variant={linkedProviders.has(preset.id) ? 'secondary' : 'primary'}
                  onClick={() => openLink(preset)}
                >
                  {linkedProviders.has(preset.id) ? 'Связать ещё' : 'Связать'}
                </Button>
              ) : preset.auth_mode === 'paired' ? (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => void openPairing(preset)}
                  disabled={issuePairing.isPending}
                >
                  Получить код
                </Button>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => openManual(preset)}>
                  Подключить
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Связывание аккаунтов: спрашиваем только адрес платформы */}
      <DrawerPanel
        open={!!linkPreset}
        title={linkPreset ? `Связать с «${linkPreset.title}»` : ''}
        onClose={() => setLinkPreset(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLinkPreset(null)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={() => void submitLink()}
              disabled={connectPartner.isPending || !form.base_url?.trim()}
            >
              {connectPartner.isPending ? 'Переходим…' : 'Продолжить'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ marginTop: 0, color: 'var(--inbx-text-tertiary)' }}>
            Вы перейдёте в свой кабинет на этой платформе и подтвердите доступ. Ключи вводить не
            нужно — после подтверждения источник данных настроится сам.
          </p>
          <Field label="Адрес платформы" required>
            <Input
              value={form.base_url ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
              placeholder="https://cabinet.example.ru"
              autoFocus
            />
          </Field>
          {linkPreset && (
            <div style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)' }}>
              Мы попросим доступ к: {linkPreset.capabilities.join(', ')}
            </div>
          )}
          {error && <div style={{ color: 'var(--inbx-danger, #b3261e)' }}>{error}</div>}
        </div>
      </DrawerPanel>

      {/* Код сопряжения: одноразовый, показывается один раз */}
      <DrawerPanel
        open={!!pairing}
        title={pairing ? `Подключить «${pairing.preset.title}»` : ''}
        onClose={() => {
          setPairing(null);
          void linksQ.refetch();
          void connectorsQ.refetch();
        }}
        footer={
          <Button
            variant="primary"
            onClick={() => {
              setPairing(null);
              void linksQ.refetch();
              void connectorsQ.refetch();
            }}
          >
            Готово
          </Button>
        }
      >
        {pairing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pairing.preset.setup_hint && (
              <p style={{ marginTop: 0, color: 'var(--inbx-text-tertiary)' }}>
                {pairing.preset.setup_hint}
              </p>
            )}
            <Field label="Код сопряжения" hint="Действует 30 минут и срабатывает один раз.">
              <textarea
                className="inbx-input"
                readOnly
                value={pairing.code}
                rows={3}
                style={{ fontFamily: 'monospace', fontSize: 12, resize: 'none' }}
                onFocus={(e) => e.currentTarget.select()}
              />
            </Field>
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(pairing.code).then(() => setCopied(true));
              }}
            >
              {copied ? 'Скопировано' : 'Скопировать'}
            </Button>
            <p style={{ margin: 0, color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
              Код не даёт доступа сам по себе: он лишь знакомит сайт с этим пространством. После
              вставки в плагин связь появится в списке «Связанные аккаунты».
            </p>
          </div>
        )}
      </DrawerPanel>

      {/* Ручной ключ: форму рисуем по описанию пресета */}
      <DrawerPanel
        open={!!manualPreset}
        title={manualPreset ? `Подключить «${manualPreset.title}»` : ''}
        onClose={() => setManualPreset(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setManualPreset(null)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={() => void submitManual()}
              disabled={createConnector.isPending || !manualRequirementsMet(manualPreset, form)}
            >
              {createConnector.isPending ? 'Подключение…' : 'Подключить'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {manualPreset?.setup_hint && (
            <p style={{ marginTop: 0, color: 'var(--inbx-text-tertiary)' }}>
              {manualPreset.setup_hint}
            </p>
          )}
          <Field label="Название" required>
            <Input
              value={form.name ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Field>
          {(manualPreset?.fields ?? []).map((field) => (
            <Field key={field.key} label={field.label} required={field.required} hint={field.hint}>
              <Input
                type={field.secret ? 'password' : 'text'}
                value={form[field.key] ?? ''}
                placeholder={field.placeholder}
                onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
              />
            </Field>
          ))}
          {error && <div style={{ color: 'var(--inbx-danger, #b3261e)' }}>{error}</div>}
        </div>
      </DrawerPanel>
    </div>
  );
}

function LinkCard({ link, slug }: { link: PartnerLinkRow; slug: string | undefined }): JSX.Element {
  const revoke = useRevokeWithConfirm(slug);
  const broken = link.status !== 'active';

  return (
    <div className="inbx-card" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{link.externalAccountName ?? link.provider}</div>
        <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          {new URL(link.baseUrl).host}
          {broken && ' · связь потеряна'}
        </div>
        {/* Причина показывается прямо здесь: иначе продавец видит пустую
            панель у оператора и не знает, что чинить. */}
        {broken && link.lastError && (
          <div style={{ color: 'var(--inbx-danger, #b3261e)', fontSize: 'var(--inbx-fz-sm)' }}>
            {link.lastError}
          </div>
        )}
      </div>
      {revoke.node}
      <Button size="sm" variant="secondary" onClick={() => revoke.ask(link.id)}>
        Отвязать
      </Button>
    </div>
  );
}

function ConnectorCard(props: {
  connector: DataConnectorRow;
  preset: ConnectorPresetRow | undefined;
  slug: string | undefined;
}): JSX.Element {
  const test = useTestDataConnector(props.slug);
  const remove = useDeleteDataConnector(props.slug);
  const [result, setResult] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);

  async function runTest(): Promise<void> {
    const res = await test.mutateAsync(props.connector.id);
    setResult(res.ok ? 'Связь есть' : `Не отвечает: ${res.error ?? res.status}`);
  }

  return (
    <div className="inbx-card" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{props.connector.name}</div>
        <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          {Object.keys(props.connector.endpoints ?? {}).length} запросов · кэш{' '}
          {props.connector.cache_ttl_sec}с
        </div>
        {result && <div style={{ fontSize: 'var(--inbx-fz-sm)' }}>{result}</div>}
      </div>
      <Button size="sm" variant="secondary" onClick={() => void runTest()} disabled={test.isPending}>
        {test.isPending ? '…' : 'Проверить'}
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setConfirm(true)}>
        Удалить
      </Button>
      <ConfirmModal
        open={confirm}
        title="Удалить источник?"
        description="Оператор перестанет видеть заказы клиента из этой системы."
        variant="danger"
        onCancel={() => setConfirm(false)}
        onConfirm={() => {
          void remove.mutateAsync(props.connector.id);
          setConfirm(false);
        }}
      />
    </div>
  );
}

/** Отвязывание необратимо для выданных токенов — всегда с подтверждением. */
function useRevokeWithConfirm(slug: string | undefined): {
  ask: (id: string) => void;
  node: JSX.Element;
} {
  const [pending, setPending] = useState<string | null>(null);
  const revoke = useRevokePartnerLink(slug);
  return {
    ask: setPending,
    node: (
      <ConfirmModal
        open={!!pending}
        title="Отвязать аккаунт?"
        description="Источники данных из этой платформы перестанут работать. Связать заново можно в любой момент."
        variant="danger"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) void revoke.mutateAsync(pending);
          setPending(null);
        }}
      />
    ),
  };
}

function presetAuthType(preset: ConnectorPresetRow): string {
  // Форма с логином — Basic, форма без логина — ключ в заголовке или в адресе.
  return preset.fields.some((f) => f.key === 'username') ? 'basic' : 'api_key';
}

function manualRequirementsMet(
  preset: ConnectorPresetRow | null,
  form: Record<string, string>,
): boolean {
  if (!preset) return false;
  if (!form.name?.trim()) return false;
  return preset.fields.every((f) => !f.required || Boolean(form[f.key]?.trim()));
}
